
import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schema?: object | object[];
  keywords?: string;
  author?: string;
  ogLocale?: string;
  twitterCard?: string;
  twitterCreator?: string;
  siteVerification?: {
    google?: string;
    bing?: string;
    yandex?: string;
  };
}

const SEO: React.FC<SEOProps> = ({ 
  title = 'IT Management Solutions | Expert IT Support & Cybersecurity for Small Businesses',
  description = 'Reliable IT support, proactive cybersecurity, and cost-effective tech management for small businesses. Local, responsive service with 24/7 monitoring. Book a free consultation today.',
  canonicalUrl = 'https://itmgmtsol.com',
  ogImage = 'https://itmgmtsol.com/wp-content/uploads/2025/05/logo1200x630-png.avif',
  ogType = 'website',
  schema,
  keywords = 'IT management, managed IT services, cybersecurity, IT support, small business IT, network support, cloud solutions',
  author = 'IT Management Solutions',
  ogLocale = 'en_US',
  twitterCard = 'summary_large_image',
  twitterCreator = '@ITMgmtSolutions',
  siteVerification
}) => {
  const siteTitle = 'IT Management Solutions';
  const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Site Verification Tags */}
      {siteVerification?.google && (
        <meta name="google-site-verification" content={siteVerification.google} />
      )}
      {siteVerification?.bing && (
        <meta name="msvalidate.01" content={siteVerification.bing} />
      )}
      {siteVerification?.yandex && (
        <meta name="yandex-verification" content={siteVerification.yandex} />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={siteTitle} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:creator" content={twitterCreator} />
      
      {/* Schema.org markup */}
      {schema && (
        Array.isArray(schema) 
          ? schema.map((schemaItem, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(schemaItem)}
              </script>
            ))
          : (
            <script type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          )
      )}
    </Helmet>
  );
};

export default SEO;
